import React, { useEffect, useRef, useState } from 'react';
import { colors, radii } from '../../theme/tokens';
import { Icon } from '../ui';
import { useToast } from '../Toast';
import { tasksApi, taskAssetUrl } from '../../lib/tasksApi';
import { Avatar } from './taskHelpers';
import useIsDesktop from '../../hooks/useIsDesktop';

// True for PDF attachments (kind flag, with a filename-extension fallback).
const isPdf = (a) => a && (a.kind === 'pdf' || /\.pdf$/i.test(a.url || ''));

// Per-task chat. Embedded messages, refresh-on-open + light polling (no
// sockets). Supports @mention autocomplete, pasting/attaching images and PDFs,
// a mobile camera-capture button, and a click-to-enlarge lightbox.
const TaskChat = ({ taskId, initialMessages = [], users = [] }) => {
  const showToast = useToast();
  const isDesktop = useIsDesktop();
  const [messages, setMessages] = useState(initialMessages);
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState([]); // [{ id, name }]
  const [suggest, setSuggest] = useState(null); // { query, items }
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{ url, w, h }]
  const [uploading, setUploading] = useState(0); // in-flight upload count
  const [lightbox, setLightbox] = useState(null); // full url or null
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    setMessages(initialMessages);
    setAttachments([]);
    setText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // Light polling while the chat is open.
  useEffect(() => {
    let alive = true;
    const id = setInterval(async () => {
      try {
        const task = await tasksApi.getTask(taskId);
        if (alive) setMessages(task.messages || []);
      } catch (_) { /* ignore transient */ }
    }, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [taskId]);

  // Close the lightbox on Escape.
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const uploadFiles = async (files) => {
    const accepted = Array.from(files || []).filter((f) => f.type.startsWith('image/') || f.type === 'application/pdf');
    if (accepted.length === 0) return;
    setUploading((n) => n + accepted.length);
    for (const file of accepted) {
      try {
        const res = await tasksApi.uploadAttachment(taskId, file);
        const kind = res.kind || (/\.pdf$/i.test(res.url) ? 'pdf' : 'image');
        setAttachments((prev) => [...prev, { url: res.url, w: res.w, h: res.h, kind, name: res.name }]);
      } catch (_) {
        showToast('Failed to upload attachment', 'error');
      } finally {
        setUploading((n) => Math.max(0, n - 1));
      }
    }
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
        const f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      uploadFiles(files);
    }
  };

  const onChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart;
    const upto = val.slice(0, caret);
    const match = /(^|\s)@(\w*)$/.exec(upto);
    if (match) {
      const query = match[2].toLowerCase();
      const items = users.filter((u) => u.name && u.name.toLowerCase().includes(query)).slice(0, 6);
      setSuggest({ query: match[2], items });
    } else {
      setSuggest(null);
    }
  };

  const pickMention = (user) => {
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : text.length;
    const before = text.slice(0, caret).replace(/@(\w*)$/, `@${user.name} `);
    const after = text.slice(caret);
    const next = before + after;
    setText(next);
    setMentions((prev) => (prev.some((m) => m.id === user._id) ? prev : [...prev, { id: user._id, name: user.name }]));
    setSuggest(null);
    if (ta) setTimeout(() => ta.focus(), 0);
  };

  const removeAttachment = (url) => setAttachments((prev) => prev.filter((a) => a.url !== url));

  const send = async (e) => {
    e.preventDefault();
    if (sending || uploading > 0) return;
    if (!text.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      const active = mentions.filter((m) => text.includes(`@${m.name}`)).map((m) => m.id);
      const task = await tasksApi.postMessage(taskId, { text: text.trim(), mentions: active, attachments });
      setMessages(task.messages || []);
      setText('');
      setMentions([]);
      setAttachments([]);
      setSuggest(null);
    } catch (_) {
      showToast('Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(e);
    }
  };

  const renderText = (msg) =>
    (msg || '').split(/(\s+)/).map((tok, i) =>
      tok.startsWith('@') ? (
        <span key={i} style={{ color: colors.parGreenBright, fontWeight: 700 }}>{tok}</span>
      ) : (
        <React.Fragment key={i}>{tok}</React.Fragment>
      )
    );

  const ordered = [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  return (
    <div style={styles.wrap}>
      <div style={styles.head}>
        <Icon name="chat" size={16} color={colors.parGreenBright} /> Discussion
      </div>

      <div style={styles.list}>
        {ordered.length === 0 ? (
          <div style={styles.empty}>No messages yet. Start the conversation.</div>
        ) : (
          ordered.map((m, i) => (
            <div key={m._id || i} style={styles.msg}>
              <Avatar user={{ name: m.userName, picture: m.userPicture }} size={28} />
              <div style={styles.msgBody}>
                <div style={styles.msgMeta}>
                  <span style={styles.msgName}>{m.userName}</span>
                  <span style={styles.msgTime}>{new Date(m.createdAt).toLocaleString()}</span>
                </div>
                {m.text ? <div style={styles.msgText}>{renderText(m.text)}</div> : null}
                {(m.attachments || []).length > 0 && (
                  <div style={styles.attachGrid}>
                    {m.attachments.map((a, idx) => (
                      isPdf(a) ? (
                        <a
                          key={idx}
                          href={taskAssetUrl(a.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.pdfCard}
                          title={a.name || 'Open PDF'}
                        >
                          <Icon name="picture_as_pdf" size={22} color="#e5484d" />
                          <span style={styles.pdfName}>{a.name || 'Document.pdf'}</span>
                          <Icon name="open_in_new" size={15} color={colors.textMuted} />
                        </a>
                      ) : (
                        <button
                          key={idx}
                          type="button"
                          style={styles.attachThumbBtn}
                          onClick={() => setLightbox(taskAssetUrl(a.url))}
                          title="Click to enlarge"
                        >
                          <img src={taskAssetUrl(a.url)} alt="attachment" style={styles.attachThumbImg} />
                        </button>
                      )
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {(attachments.length > 0 || uploading > 0) && (
        <div style={styles.pendingRow}>
          {attachments.map((a) => (
            isPdf(a) ? (
              <div key={a.url} style={styles.pendingPdf}>
                <Icon name="picture_as_pdf" size={20} color="#e5484d" />
                <span style={styles.pendingPdfName}>{a.name || 'Document.pdf'}</span>
                <button type="button" style={styles.pendingRemove} onClick={() => removeAttachment(a.url)} title="Remove">
                  <Icon name="close" size={13} color={colors.white} />
                </button>
              </div>
            ) : (
              <div key={a.url} style={styles.pendingThumb}>
                <img src={taskAssetUrl(a.url)} alt="" style={styles.pendingImg} />
                <button type="button" style={styles.pendingRemove} onClick={() => removeAttachment(a.url)} title="Remove">
                  <Icon name="close" size={13} color={colors.white} />
                </button>
              </div>
            )
          ))}
          {uploading > 0 && <div style={styles.pendingLoading}>Uploading…</div>}
        </div>
      )}

      <form onSubmit={send} style={styles.composer}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }}
        />
        <button type="button" style={styles.attachBtn} onClick={() => fileRef.current && fileRef.current.click()} title="Attach image or PDF">
          <Icon name="attach_file" size={20} color={colors.textMuted} />
        </button>
        {!isDesktop && (
          <button type="button" style={styles.attachBtn} onClick={() => cameraRef.current && cameraRef.current.click()} title="Take a photo">
            <Icon name="photo_camera" size={20} color={colors.textMuted} />
          </button>
        )}
        <div style={{ position: 'relative', flex: 1 }}>
          <textarea
            ref={taRef}
            value={text}
            onChange={onChange}
            onPaste={onPaste}
            onKeyDown={onKeyDown}
            placeholder="Write a message… use @ to mention, paste or attach images / PDFs"
            rows={2}
            style={styles.textarea}
          />
          {suggest && suggest.items.length > 0 && (
            <div style={styles.suggest}>
              {suggest.items.map((u) => (
                <button key={u._id} type="button" style={styles.suggestItem} onClick={() => pickMention(u)}>
                  <Avatar user={u} size={22} /> {u.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="submit" disabled={sending || uploading > 0 || (!text.trim() && attachments.length === 0)} style={styles.send}>
          <Icon name="send" size={18} color={colors.white} />
        </button>
      </form>

      {lightbox && (
        <div style={styles.lightbox} onClick={() => setLightbox(null)}>
          <button type="button" style={styles.lightboxClose} onClick={() => setLightbox(null)} title="Close">
            <Icon name="close" size={26} color={colors.white} />
          </button>
          <img src={lightbox} alt="attachment" style={styles.lightboxImg} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: { marginTop: 14, borderTop: `1px solid ${colors.borderSoft}`, paddingTop: 12 },
  head: { display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, color: colors.text, marginBottom: 10 },
  list: { display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 320, overflowY: 'auto', marginBottom: 10 },
  empty: { color: colors.textFaint, fontSize: 13, padding: '8px 0' },
  msg: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  msgBody: { flex: 1, minWidth: 0 },
  msgMeta: { display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  msgName: { fontWeight: 700, fontSize: 13, color: colors.text },
  msgTime: { fontSize: 11, color: colors.textFaint },
  msgText: { fontSize: 14, color: colors.text, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  attachGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  attachThumbBtn: { padding: 0, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.sm, background: 'transparent', cursor: 'pointer', lineHeight: 0, overflow: 'hidden' },
  attachThumbImg: { maxWidth: 220, maxHeight: 200, display: 'block', objectFit: 'cover' },
  pdfCard: { display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: 260, padding: '8px 10px', borderRadius: radii.sm, border: `1px solid ${colors.glassBorder}`, background: colors.surfaceSoft, color: colors.text, textDecoration: 'none', fontSize: 13, fontWeight: 600 },
  pdfName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pendingRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  pendingThumb: { position: 'relative', width: 64, height: 64, borderRadius: radii.sm, overflow: 'hidden', border: `1px solid ${colors.glassBorder}` },
  pendingPdf: { position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 190, height: 40, padding: '0 26px 0 10px', borderRadius: radii.sm, border: `1px solid ${colors.glassBorder}`, background: colors.surfaceSoft },
  pendingPdfName: { fontSize: 12, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pendingImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  pendingRemove: {
    position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%',
    border: 'none', background: 'rgba(0,0,0,0.6)', cursor: 'pointer', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', padding: 0
  },
  pendingLoading: { fontSize: 12, color: colors.textMuted },
  composer: { display: 'flex', gap: 8, alignItems: 'flex-end' },
  attachBtn: {
    width: 42, height: 42, flexShrink: 0, borderRadius: radii.sm,
    border: `1px solid ${colors.border}`, background: colors.inputBg, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: radii.sm,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.text,
    fontSize: 14,
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  suggest: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    marginBottom: 4,
    background: colors.menuBg,
    border: `1px solid ${colors.border}`,
    borderRadius: radii.md,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    zIndex: 10,
    overflow: 'hidden',
    minWidth: 180
  },
  suggestItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    color: colors.text,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left'
  },
  send: {
    width: 42,
    height: 42,
    flexShrink: 0,
    borderRadius: radii.sm,
    border: 'none',
    background: colors.parGreen,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  lightbox: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    cursor: 'zoom-out'
  },
  lightboxImg: { maxWidth: '92vw', maxHeight: '90vh', borderRadius: radii.md, boxShadow: '0 20px 60px rgba(0,0,0,0.6)', cursor: 'default' },
  lightboxClose: {
    position: 'absolute', top: 18, right: 20, width: 44, height: 44, borderRadius: '50%',
    border: 'none', background: 'rgba(255,255,255,0.12)', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
  }
};

export default TaskChat;
